import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import mesApi, { FlourQualityQueueItem } from "../../../Api/mes.api";
import FlourQualityShell from "./components/FlourQualityShell";
import { deviceSummaryBadge, formatHrDate } from "./flourQualityUtils";
import { t } from "../../../i18n/locale";

type QueueTab = "flour" | "other";

const FlourQualityQueue = () => {
  const [queue, setQueue] = useState<FlourQualityQueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [approvingLotId, setApprovingLotId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<QueueTab>("flour");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await mesApi.getFlourQualityQueue();
      setQueue(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const msg = err.message || t("Greška pri učitavanju reda čekanja");
      setLoadError(msg);
      setQueue([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleApprove = useCallback(
    async (lotId: number) => {
      try {
        setApprovingLotId(lotId);
        await mesApi.releaseMaterialLot(lotId, "operator");
        toast.success(t("Stavka odobrena — puštena iz karantene u zalihu."));
        await loadQueue();
      } catch (err: any) {
        toast.error(t("Odobravanje nije uspjelo: {message}", { message: err.message }));
      } finally {
        setApprovingLotId(null);
      }
    },
    [loadQueue]
  );

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const flourQueue = queue.filter((row) => row.is_flour);
  const otherQueue = queue.filter((row) => !row.is_flour);
  const rows = activeTab === "flour" ? flourQueue : otherQueue;

  // Selection is per tab: switching tabs would otherwise keep hidden rows
  // selected and silently approve them.
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const switchTab = (tab: QueueTab) => {
    setActiveTab(tab);
    clearSelection();
  };

  const toggleSelect = (lotId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId);
      else next.add(lotId);
      return next;
    });
  };

  const allVisibleSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.lot_id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) clearSelection();
    else setSelectedIds(new Set(rows.map((row) => row.lot_id)));
  };

  const handleBulkApprove = async () => {
    const lotIds = rows.map((row) => row.lot_id).filter((id) => selectedIds.has(id));
    if (lotIds.length === 0) return;

    setBulkApproving(true);
    try {
      const results = await Promise.allSettled(
        lotIds.map((lotId) => mesApi.releaseMaterialLot(lotId, "operator"))
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const approved = results.length - failed;

      if (approved > 0) {
        toast.success(t("{count} stavki odobreno — puštene iz karantene u zalihu.", { count: approved }));
      }
      if (failed > 0) {
        toast.error(t("{count} stavki nije odobreno.", { count: failed }));
      }
      clearSelection();
      await loadQueue();
    } finally {
      setBulkApproving(false);
    }
  };

  const renderSelectAllHeader = () => (
    <th style={{ width: 32 }}>
      <input
        type="checkbox"
        checked={allVisibleSelected}
        onChange={toggleSelectAll}
        title={t("Odaberi sve")}
      />
    </th>
  );

  const renderSelectCell = (row: FlourQualityQueueItem) => (
    <td>
      <input
        type="checkbox"
        checked={selectedIds.has(row.lot_id)}
        onChange={() => toggleSelect(row.lot_id)}
      />
    </td>
  );

  const renderApproveCell = (row: FlourQualityQueueItem) => (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
      <button
        type="button"
        className="btn-save btn-sm"
        disabled={approvingLotId === row.lot_id}
        onClick={() => handleApprove(row.lot_id)}
      >
        {approvingLotId === row.lot_id ? t("Odobravanje…") : t("Odobri")}
      </button>
      {row.is_flour && (
        <Link to={`/mes/flour-analyses/lot/${row.lot_id}`} className="btn-primary btn-sm">
          {t("Analiza")}
        </Link>
      )}
    </div>
  );

  return (
    <FlourQualityShell>
      <ToastContainer position="top-center" autoClose={2500} />

      <div className="mes-section">
        <h3 style={{ margin: 0, border: "none", padding: 0 }}>{t("Red čekanja — karantena")}</h3>
        <p className="iwt-role-hint" style={{ marginTop: 8, marginBottom: 0 }}>
          {t("Artikli u karanteni nakon primke. Kliknite")} <strong>{t("Odobri")}</strong> {t("za ručno puštanje stavke u zalihu, ili označite više stavki i odobrite ih odjednom.")}
          {" "}
          {t("Za brašno dodatno otvorite")} <strong>{t("Analizu")}</strong> {t("za pregled blend-a, mjerenja i QC odluku.")}
        </p>
      </div>

      <div className="mes-section">
        <div
          className="fq-subnav"
          role="tablist"
          style={{ marginBottom: 16 }}
        >
          <button
            type="button"
            role="tab"
            className={activeTab === "flour" ? "active" : ""}
            onClick={() => switchTab("flour")}
          >
            {t("Brašno")} ({flourQueue.length})
          </button>
          <button
            type="button"
            role="tab"
            className={activeTab === "other" ? "active" : ""}
            onClick={() => switchTab("other")}
          >
            {t("Ostali artikli")} ({otherQueue.length})
          </button>
        </div>

        <div className="wh-pregled-toolbar" style={{ marginBottom: 16 }}>
          <button type="button" className="btn-secondary btn-sm" onClick={loadQueue} disabled={loading}>
            {t("Osvježi")}
          </button>
        </div>

        {selectedIds.size > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 16,
              padding: "10px 14px",
              borderRadius: 6,
              background: "rgba(37,99,235,0.08)",
              border: "1px solid rgba(37,99,235,0.35)",
            }}
          >
            <strong>{t("{count} odabrano", { count: selectedIds.size })}</strong>
            <button
              type="button"
              className="btn-save btn-sm"
              disabled={bulkApproving}
              onClick={handleBulkApprove}
            >
              {bulkApproving ? t("Odobravanje…") : t("Odobri odabrano ({count})", { count: selectedIds.size })}
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={bulkApproving}
              onClick={clearSelection}
            >
              {t("Odustani")}
            </button>
          </div>
        )}

        {loadError && (
          <p className="mes-hint" style={{ color: "#dc3545" }}>
            {t("{message} — provjerite je li Hive pokrenut.", { message: loadError })}
          </p>
        )}

        {loading && queue.length === 0 && !loadError ? (
          <p>{t("Učitavanje…")}</p>
        ) : !loadError && rows.length === 0 ? (
          <p className="mes-hint">
            {activeTab === "flour" ? t("Nema brašna u karanteni.") : t("Nema ostalih artikala u karanteni.")}
          </p>
        ) : rows.length > 0 ? (
          activeTab === "flour" ? (
            <div className="mes-table-wrap">
              <table className="mes-table">
              <thead>
                <tr>
                  {renderSelectAllHeader()}
                  <th>{t("LOT")}</th>
                  <th>{t("Sirovina")}</th>
                  <th>{t("Dobavljač")}</th>
                  <th>{t("Primka")}</th>
                  <th>{t("Datum")}</th>
                  <th>{t("kg")}</th>
                  <th>{t("Farinograf")}</th>
                  <th>{t("Extensograf")}</th>
                  <th>{t("Amylograf")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.lot_id} className={selectedIds.has(row.lot_id) ? "selected-row" : ""}>
                    {renderSelectCell(row)}
                    <td>
                      <strong>{row.lot_number_supplier || row.internal_lot_code || row.lot_id}</strong>
                    </td>
                    <td>{row.ingredient_name || "—"}</td>
                    <td>{row.supplier_name || "—"}</td>
                    <td>{row.delivery_note_number || "—"}</td>
                    <td>{row.arrival_date ? formatHrDate(row.arrival_date) : "—"}</td>
                    <td className="fq-num">{row.quantity_received_kg?.toFixed(2)}</td>
                    <td>
                      <span className={deviceSummaryBadge(row.measurements?.farinograph).className}>
                        {deviceSummaryBadge(row.measurements?.farinograph).label}
                      </span>
                    </td>
                    <td>
                      <span className={deviceSummaryBadge(row.measurements?.extensograph).className}>
                        {deviceSummaryBadge(row.measurements?.extensograph).label}
                      </span>
                    </td>
                    <td>
                      <span className={deviceSummaryBadge(row.measurements?.amylograph).className}>
                        {deviceSummaryBadge(row.measurements?.amylograph).label}
                      </span>
                    </td>
                    <td>{renderApproveCell(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <div className="mes-table-wrap">
              <table className="mes-table">
              <thead>
                <tr>
                  {renderSelectAllHeader()}
                  <th>{t("LOT")}</th>
                  <th>{t("Sirovina")}</th>
                  <th>{t("Tip")}</th>
                  <th>{t("Dobavljač")}</th>
                  <th>{t("Primka")}</th>
                  <th>{t("Datum")}</th>
                  <th>{t("kg")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.lot_id} className={selectedIds.has(row.lot_id) ? "selected-row" : ""}>
                    {renderSelectCell(row)}
                    <td>
                      <strong>{row.lot_number_supplier || row.internal_lot_code || row.lot_id}</strong>
                    </td>
                    <td>{row.ingredient_name || "—"}</td>
                    <td>{row.ingredient_type || "—"}</td>
                    <td>{row.supplier_name || "—"}</td>
                    <td>{row.delivery_note_number || "—"}</td>
                    <td>{row.arrival_date ? formatHrDate(row.arrival_date) : "—"}</td>
                    <td className="fq-num">{row.quantity_received_kg?.toFixed(2)}</td>
                    <td>{renderApproveCell(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )
        ) : null}
      </div>
    </FlourQualityShell>
  );
};

export default FlourQualityQueue;
